import React from "react";

export const smoothstep = (x: number) => x * x * (3 - 2 * x);

/**
 * The hero's hand-off to Featured Work (app.js `onScroll`). The hero is fixed,
 * so it doesn't move with the page: its copy fades and lifts over the first
 * 0.8 of a viewport of scroll while Work rises over it, and the glow fades on
 * the same scroll. Written straight to the DOM, as scroll is per frame.
 *
 * Attach `heroRef` to the fixed layer: it gets opacity, transform, and
 * `inert` once invisible (an invisible fixed layer would still take the
 * pointer and focus). Attach `rootRef` to an ancestor of the glow: it gets
 * `--glow-scroll`, multiplied with the intro's factor by the glow's CSS.
 */
export const useHeroScroll = () => {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const heroRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const update = () => {
            // The layout viewport: innerHeight changes as a phone's toolbar
            // slides, which would walk the fade backwards mid-gesture
            const vh = Math.max(
                1,
                document.documentElement.clientHeight || window.innerHeight,
            );
            const t = Math.min(1, Math.max(0, window.scrollY / (vh * 0.8)));

            const hero = heroRef.current;
            if (hero) {
                const e = smoothstep(Math.min(1, t * 1.05));
                hero.style.opacity = String(1 - e);
                hero.style.transform = `translate3d(0, ${(-e * vh * 0.075).toFixed(1)}px, 0)`;
                hero.inert = e > 0.99;
            }
            rootRef.current?.style.setProperty(
                "--glow-scroll",
                Math.max(0, 1 - smoothstep(t) * 1.06).toFixed(3),
            );
        };
        update();
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update, { passive: true });
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, []);

    return { rootRef, heroRef };
};
